import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { staffLogin, staffOtpVerify, otpEmailSend, otpEmailVerify, type LoginChallenge } from '../api/auth'
import { useSession } from '../store/session'
import { BRAND } from '../brand'

type Phase = 'creds' | 'mfa'
type Method = 'app' | 'email'

export function Login() {
  const nav = useNavigate()
  const onAuthenticated = useSession((s) => s.onAuthenticated)

  const [phase, setPhase] = useState<Phase>('creds')
  const [method, setMethod] = useState<Method>('app')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [challenge, setChallenge] = useState<LoginChallenge | null>(null)
  const [sentTo, setSentTo] = useState('')
  const [devCode, setDevCode] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  async function submitCreds() {
    setErr('')
    setBusy(true)
    try {
      const ch = await staffLogin(email.trim(), password)
      setChallenge(ch)
      setMethod('app')
      setCode('')
      setPhase('mfa')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Sign in failed')
    } finally {
      setBusy(false)
    }
  }

  async function submitMfa() {
    if (!challenge) return
    setErr('')
    setBusy(true)
    try {
      if (method === 'email') await otpEmailVerify(challenge.challenge, code.trim())
      else await staffOtpVerify(challenge.challenge, code.trim())
      await onAuthenticated()
      nav('/', { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Verification failed')
    } finally {
      setBusy(false)
    }
  }

  async function sendEmailCode() {
    if (!challenge) return
    setErr('')
    setBusy(true)
    try {
      const res = await otpEmailSend(challenge.challenge)
      setSentTo(res.sent_to)
      setDevCode(res.dev_code ?? '')
      setMethod('email')
      setCode('')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not send email code')
    } finally {
      setBusy(false)
    }
  }

  function useAuthenticator() {
    setMethod('app')
    setCode('')
    setErr('')
  }

  function backToCreds() {
    setPhase('creds')
    setChallenge(null)
    setMethod('app')
    setCode('')
    setSentTo('')
    setDevCode('')
    setErr('')
  }

  const isEnroll = challenge?.step === 'enroll'
  const title =
    method === 'email' ? 'Enter the emailed code' : isEnroll ? 'Set up your authenticator' : 'Enter your verification code'
  const lead =
    method === 'email'
      ? `We sent a 6-digit code to ${sentTo}`
      : isEnroll
        ? 'Scan the QR code with an authenticator app, then enter the 6-digit code.'
        : `6-digit code from your authenticator app for ${email}`

  return (
    <div className="auth-wrap">
      <div className="auth-col">
        <div className="auth-brand">
          <div className="mark" />
          <div className="name">{BRAND.name}</div>
          <div className="tag">Console</div>
        </div>

        <div className="auth-card">
          {phase === 'creds' && (
            <>
              <div>
                <h1>Sign in</h1>
                <div className="lead">Payment rails monitoring and configuration</div>
              </div>
              <label className="auth-field">
                <span>Work email</span>
                <input
                  className="auth-input"
                  aria-label="Work email"
                  value={email}
                  autoFocus
                  placeholder="you@company.co.ke"
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitCreds()}
                />
              </label>
              <label className="auth-field">
                <span>Password</span>
                <input
                  className="auth-input mono"
                  aria-label="Password"
                  type="password"
                  value={password}
                  placeholder="••••••••••"
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitCreds()}
                />
              </label>
              {err && <div className="auth-error">{err}</div>}
              <button className="btn primary" disabled={busy || !email || !password} onClick={submitCreds}>
                {busy ? 'Checking…' : 'Continue'}
              </button>
              <div className="auth-hint">A verification code is required on every sign-in. Sessions expire after inactivity.</div>
            </>
          )}

          {phase === 'mfa' && challenge && (
            <>
              <div>
                <h1>{title}</h1>
                <div className="lead">{lead}</div>
              </div>

              {method === 'app' && isEnroll && challenge.otpauth_uri && (
                <div className="auth-qr">
                  <div style={{ background: '#fff', padding: 8, borderRadius: 8, lineHeight: 0 }}>
                    <QRCodeSVG value={challenge.otpauth_uri} size={168} aria-label="Authenticator QR code" />
                  </div>
                  <div className="auth-secret">{challenge.secret}</div>
                </div>
              )}

              <input
                className="auth-input auth-code"
                aria-label="Verification code"
                value={code}
                autoFocus
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && submitMfa()}
              />
              {method === 'email' && devCode && (
                <div className="auth-hint">Dev: your code is <span className="mono">{devCode}</span> (also in Mailpit).</div>
              )}
              {err && <div className="auth-error">{err}</div>}
              <button className="btn primary" disabled={busy || code.length < 6} onClick={submitMfa}>
                {busy ? 'Verifying…' : 'Verify and open console'}
              </button>

              {method === 'app' ? (
                <button className="btn secondary" disabled={busy} onClick={sendEmailCode}>
                  Email me a code instead
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn secondary" style={{ flex: 1 }} disabled={busy} onClick={sendEmailCode}>
                    Resend code
                  </button>
                  <button className="btn ghost" style={{ flex: 1 }} disabled={busy} onClick={useAuthenticator}>
                    Use authenticator
                  </button>
                </div>
              )}

              <button className="btn ghost" onClick={backToCreds}>
                Use a different account
              </button>
            </>
          )}
        </div>

        <div className="auth-foot">
          Lost your device? Use an email code or contact a system administrator to reset MFA.
        </div>
      </div>
    </div>
  )
}
